// Battle setup screen - select gladiators for battle

import { Canvas } from '../Canvas';
import { CONSTANTS, ScreenState } from '../types';
import { Screen, ScreenContext } from './Screen';
import { GameState, getCurrentTeam } from '../systems/GameState';
import { GAME_CONSTANTS } from '../data/constants';
import { isHealthy } from '../entities/Gladiator';
import { getBattleTeamSize, battleTeamHasGladiator, addToBattleSlot, removeFromBattleSlot, getNextEmptyBattleSlot } from '../entities/Team';

export class BattleSetup implements Screen {
  readonly state = ScreenState.BATTLE_SETUP;
  private context: ScreenContext;
  private gameState: GameState | null = null;
  private selectedRosterIndex: number = 0;
  private selectedBattleSlot: number = -1;

  constructor(context: ScreenContext) {
    this.context = context;
  }

  setGameState(gameState: GameState): void {
    this.gameState = gameState;
  }

  enter(): void {
    console.log('Entering BattleSetup');
    this.selectedRosterIndex = 0;
    this.selectedBattleSlot = -1;
  }

  exit(): void {
    console.log('Exiting BattleSetup');
  }

  update(_deltaTime: number): void {}

  render(canvas: Canvas): void {
    if (!this.gameState) {
      canvas.clear(CONSTANTS.BATTLE_SETUP_BACKGROUND);
      return;
    }

    const team = getCurrentTeam(this.gameState);
    if (!team) {
      canvas.clear(CONSTANTS.BATTLE_SETUP_BACKGROUND);
      canvas.drawText('No team selected', 400, 260, CONSTANTS.BIG_FONT, '#ffffff', 'center');
      return;
    }

    canvas.clear(CONSTANTS.BATTLE_SETUP_BACKGROUND);

    // Header
    this.renderHeader(canvas, team);

    // Team roster (left side)
    this.renderRoster(canvas, team);

    // Battle formation (right side)
    this.renderBattleFormation(canvas, team);

    // Instructions
    this.renderInstructions(canvas, team);

    // Buttons
    this.renderButtons(canvas, team);
  }

  private renderHeader(canvas: Canvas, team: any): void {
    canvas.drawRect(0, 0, CONSTANTS.CANVAS_WIDTH, 50, '#000040');

    canvas.drawText('Taistelujoukon valinta', 20, 12, CONSTANTS.BIG_FONT, '#ffffff');
    canvas.drawText(team.name, CONSTANTS.CANVAS_WIDTH / 2, 12, CONSTANTS.BIG_FONT, '#ffff00', 'center');

    const battleSize = getBattleTeamSize(team);
    canvas.drawText(
      `Taistelujoukko: ${battleSize}/${GAME_CONSTANTS.MAX_GLADIATORS_IN_BATTLE}`,
      CONSTANTS.CANVAS_WIDTH - 20,
      12,
      CONSTANTS.BIG_FONT,
      battleSize > 0 ? '#00ff00' : '#ff6666',
      'right'
    );
  }

  private renderRoster(canvas: Canvas, team: any): void {
    const rosterX = 20;
    const rosterY = 60;
    const rosterWidth = 300;
    const rosterHeight = 380;

    canvas.drawRect(rosterX, rosterY, rosterWidth, rosterHeight, '#000066');
    canvas.drawRectOutline(rosterX, rosterY, rosterWidth, rosterHeight, '#6666ff');

    canvas.drawText('Joukkueen gladiaattorit', rosterX + 10, rosterY + 8, CONSTANTS.MAIN_FONT, '#ffffff');

    const itemHeight = 50;
    const startY = rosterY + 35;

    for (let i = 0; i < team.gladiatorIds.length; i++) {
      const gladiator = this.gameState!.gladiators.get(team.gladiatorIds[i]);
      if (!gladiator) continue;

      const itemY = startY + i * itemHeight;
      const inBattle = battleTeamHasGladiator(team, gladiator.id);
      const healthy = isHealthy(gladiator);

      // Selection highlight
      if (i === this.selectedRosterIndex) {
        canvas.drawRect(rosterX + 2, itemY, rosterWidth - 4, itemHeight - 2, '#0000aa');
        // Add extra border if ready to place
        if (healthy && !inBattle && getBattleTeamSize(team) < GAME_CONSTANTS.MAX_GLADIATORS_IN_BATTLE) {
          canvas.drawRectOutline(rosterX + 2, itemY, rosterWidth - 4, itemHeight - 2, '#00ff00', 2);
        }
      }

      // Portrait
      const portrait = this.context.assets.gladiators[gladiator.race.id];
      if (portrait) {
        canvas.drawImage(portrait, rosterX + 8, itemY + 5, 40, 40);
      }

      // Status overlay
      if (inBattle) {
        canvas.drawRect(rosterX + 8, itemY + 5, 40, 40, 'rgba(0, 255, 0, 0.3)');
      }
      if (!healthy) {
        canvas.drawRect(rosterX + 8, itemY + 5, 40, 40, 'rgba(255, 0, 0, 0.5)');
      }

      // Name and info
      canvas.drawText(gladiator.name, rosterX + 55, itemY + 8, CONSTANTS.MAIN_FONT, '#ffffff');
      canvas.drawText(gladiator.race.name, rosterX + 55, itemY + 24, CONSTANTS.MSG_FONT, '#aaaaaa');

      // Stats summary
      canvas.drawText(
        `HP:${gladiator.hp} STR:${gladiator.str} SPD:${gladiator.speed}`,
        rosterX + 55,
        itemY + 36,
        CONSTANTS.MSG_FONT,
        '#88ff88'
      );

      // Status indicator
      if (!healthy) {
        canvas.drawText(`Loukkaantunut (${gladiator.hurt}kk)`, rosterX + rosterWidth - 10, itemY + 20, CONSTANTS.MSG_FONT, '#ff6666', 'right');
      } else if (inBattle) {
        canvas.drawText('Taistelussa', rosterX + rosterWidth - 10, itemY + 20, CONSTANTS.MSG_FONT, '#00ff00', 'right');
      }

      // Click region
      canvas.addClickableRegion({
        id: `roster-${i}`,
        x: rosterX,
        y: itemY,
        width: rosterWidth,
        height: itemHeight,
        onClick: () => {
          this.selectedRosterIndex = i;
          this.selectedBattleSlot = -1;
        },
      });
    }
  }

  private renderBattleFormation(canvas: Canvas, team: any): void {
    const formX = 340;
    const formY = 60;
    const formWidth = 435;
    const formHeight = 380;

    canvas.drawRect(formX, formY, formWidth, formHeight, '#003300');
    canvas.drawRectOutline(formX, formY, formWidth, formHeight, '#66ff66');

    canvas.drawText('Taistelumuodostelma', formX + formWidth / 2, formY + 8, CONSTANTS.MAIN_FONT, '#ffffff', 'center');

    // Battle slots (2 rows of 3)
    const slotWidth = 130;
    const slotHeight = 150;
    const slotSpacingX = 10;
    const slotSpacingY = 20;
    const startX = formX + 15;
    const startY = formY + 40;

    for (let i = 0; i < GAME_CONSTANTS.MAX_GLADIATORS_IN_BATTLE; i++) {
      const row = Math.floor(i / 3);
      const col = i % 3;
      const slotX = startX + col * (slotWidth + slotSpacingX);
      const slotY = startY + row * (slotHeight + slotSpacingY);

      const gladiatorId = team.battleGladiatorIds[i];
      const gladiator = gladiatorId ? this.gameState!.gladiators.get(gladiatorId) : null;

      // Slot background
      const isSelected = i === this.selectedBattleSlot;
      const isDropTarget = !gladiator && this.hasSelectedHealthyGladiator();
      const bgColor = isSelected ? '#006600' : (isDropTarget ? '#003322' : (gladiator ? '#004400' : '#002200'));
      canvas.drawRect(slotX, slotY, slotWidth, slotHeight, bgColor);

      // Animated border for drop targets
      if (isDropTarget) {
        canvas.drawRectOutline(slotX, slotY, slotWidth, slotHeight, '#00ff00', 2);
      } else {
        canvas.drawRectOutline(slotX, slotY, slotWidth, slotHeight, '#00aa00');
      }

      // Slot number
      canvas.drawText(`${i + 1}`, slotX + 5, slotY + 5, CONSTANTS.MSG_FONT, '#666666');

      if (gladiator) {
        // Portrait (larger)
        const portrait = this.context.assets.gladiators[gladiator.race.id];
        if (portrait) {
          canvas.drawImage(portrait, slotX + (slotWidth - 64) / 2, slotY + 20, 64, 64);
        }

        // Name and stats
        canvas.drawText(gladiator.name, slotX + slotWidth / 2, slotY + 90, CONSTANTS.MAIN_FONT, '#ffffff', 'center');
        canvas.drawText(gladiator.race.name, slotX + slotWidth / 2, slotY + 105, CONSTANTS.MSG_FONT, '#aaaaaa', 'center');

        canvas.drawText(
          `HP:${gladiator.hp} STR:${gladiator.str}`,
          slotX + slotWidth / 2,
          slotY + 125,
          CONSTANTS.MSG_FONT,
          '#88ff88',
          'center'
        );

        // Remove button
        canvas.drawButton(
          `remove-${i}`,
          'Poista',
          slotX + slotWidth - 55,
          slotY + 5,
          50,
          20,
          CONSTANTS.MSG_FONT,
          '#ffffff',
          '#aa0000',
          () => this.removeFromBattle(i)
        );
      } else {
        // Empty slot - show drop indicator if gladiator selected
        if (isDropTarget) {
          canvas.drawText('+ Lisää', slotX + slotWidth / 2, slotY + slotHeight / 2 - 10, CONSTANTS.MAIN_FONT, '#88ff88', 'center');
          canvas.drawText('klikkaamalla', slotX + slotWidth / 2, slotY + slotHeight / 2 + 8, CONSTANTS.MSG_FONT, '#66cc66', 'center');
        } else {
          canvas.drawText('Tyhjä', slotX + slotWidth / 2, slotY + slotHeight / 2, CONSTANTS.MAIN_FONT, '#446644', 'center');
        }
      }

      // Click region
      canvas.addClickableRegion({
        id: `slot-${i}`,
        x: slotX,
        y: slotY,
        width: slotWidth,
        height: slotHeight,
        onClick: () => {
          this.selectedBattleSlot = i;
          this.tryAddSelectedToBattle();
        },
      });
    }
  }

  private renderInstructions(canvas: Canvas, team: any): void {
    const instrY = CONSTANTS.CANVAS_HEIGHT - 90;

    canvas.drawRect(0, instrY, CONSTANTS.CANVAS_WIDTH, 40, '#000040');

    const battleSize = getBattleTeamSize(team);

    if (battleSize === 0) {
      canvas.drawText(
        'Valitse gladiaattorit taisteluun klikkaamalla heitä ja sitten tyhjää paikkaa',
        CONSTANTS.CANVAS_WIDTH / 2,
        instrY + 12,
        CONSTANTS.MAIN_FONT,
        '#ffff66',
        'center'
      );
    } else if (battleSize < GAME_CONSTANTS.MAX_GLADIATORS_IN_BATTLE) {
      canvas.drawText(
        `Voit lisätä vielä ${GAME_CONSTANTS.MAX_GLADIATORS_IN_BATTLE - battleSize} gladiaattoria`,
        CONSTANTS.CANVAS_WIDTH / 2,
        instrY + 12,
        CONSTANTS.MAIN_FONT,
        '#88ff88',
        'center'
      );
    } else {
      canvas.drawText(
        'Taistelujoukko täynnä! Paina "Aloita taistelu" jatkaaksesi.',
        CONSTANTS.CANVAS_WIDTH / 2,
        instrY + 12,
        CONSTANTS.MAIN_FONT,
        '#00ff00',
        'center'
      );
    }
  }

  private renderButtons(canvas: Canvas, team: any): void {
    const btnY = CONSTANTS.CANVAS_HEIGHT - 50;

    // Back button
    canvas.drawButton(
      'back',
      'Takaisin',
      20,
      btnY,
      100,
      35,
      CONSTANTS.MAIN_BUTTON_FONT,
      '#ffffff',
      '#666666',
      () => this.context.onScreenChange(ScreenState.MAIN_MENU)
    );

    // Auto-fill button
    canvas.drawButton(
      'auto-fill',
      'Täytä automaattisesti',
      140,
      btnY,
      150,
      35,
      CONSTANTS.MAIN_BUTTON_FONT,
      '#ffffff',
      '#006666',
      () => this.autoFillBattleTeam(team)
    );

    // Clear button
    canvas.drawButton(
      'clear',
      'Tyhjennä',
      310,
      btnY,
      90,
      35,
      CONSTANTS.MAIN_BUTTON_FONT,
      '#ffffff',
      '#660000',
      () => this.clearBattleTeam(team)
    );

    // Start battle button
    const battleSize = getBattleTeamSize(team);
    if (battleSize > 0) {
      canvas.drawButton(
        'start-battle',
        'Aloita taistelu',
        CONSTANTS.CANVAS_WIDTH - 150,
        btnY,
        130,
        35,
        CONSTANTS.MAIN_BUTTON_FONT,
        '#ffffff',
        '#008800',
        () => this.startBattle()
      );
    }
  }

  private hasSelectedHealthyGladiator(): boolean {
    const team = getCurrentTeam(this.gameState!);
    if (!team) return false;

    const gladiatorId = team.gladiatorIds[this.selectedRosterIndex];
    if (!gladiatorId) return false;

    const gladiator = this.gameState!.gladiators.get(gladiatorId);
    if (!gladiator || !isHealthy(gladiator)) return false;

    // Check if already in battle
    if (battleTeamHasGladiator(team, gladiatorId)) return false;

    // Check if battle team is full
    if (getBattleTeamSize(team) >= GAME_CONSTANTS.MAX_GLADIATORS_IN_BATTLE) return false;

    return true;
  }

  private tryAddSelectedToBattle(): void {
    const team = getCurrentTeam(this.gameState!);
    if (!team) return;

    const gladiatorId = team.gladiatorIds[this.selectedRosterIndex];
    if (!gladiatorId) return;

    const gladiator = this.gameState!.gladiators.get(gladiatorId);
    if (!gladiator || !isHealthy(gladiator)) return;

    // Don't add if already in battle
    if (battleTeamHasGladiator(team, gladiatorId)) return;

    // Check if battle team is full
    if (getBattleTeamSize(team) >= GAME_CONSTANTS.MAX_GLADIATORS_IN_BATTLE) return;

    // Add to selected slot or next empty slot
    const slot = this.selectedBattleSlot >= 0 ? this.selectedBattleSlot : getNextEmptyBattleSlot(team);
    if (slot >= 0 && team.battleGladiatorIds[slot] === null) {
      addToBattleSlot(team, gladiatorId, slot);
      console.log(`Added ${gladiator.name} to battle slot ${slot + 1}`);
    }
  }

  private removeFromBattle(slot: number): void {
    const team = getCurrentTeam(this.gameState!);
    if (!team) return;

    removeFromBattleSlot(team, slot);
    console.log(`Removed gladiator from battle slot ${slot + 1}`);
  }

  private autoFillBattleTeam(team: any): void {
    for (const gladiatorId of team.gladiatorIds) {
      if (getBattleTeamSize(team) >= GAME_CONSTANTS.MAX_GLADIATORS_IN_BATTLE) break;

      const gladiator = this.gameState!.gladiators.get(gladiatorId);
      if (!gladiator || !isHealthy(gladiator)) continue;
      if (battleTeamHasGladiator(team, gladiatorId)) continue;

      const slot = getNextEmptyBattleSlot(team);
      if (slot >= 0) {
        addToBattleSlot(team, gladiatorId, slot);
      }
    }
    console.log('Auto-filled battle team');
  }

  private clearBattleTeam(team: any): void {
    for (let i = 0; i < GAME_CONSTANTS.MAX_GLADIATORS_IN_BATTLE; i++) {
      removeFromBattleSlot(team, i);
    }
    console.log('Cleared battle team');
  }

  private startBattle(): void {
    const team = getCurrentTeam(this.gameState!);
    if (!team || getBattleTeamSize(team) === 0) return;

    console.log('Starting battle!');
    this.context.onScreenChange(ScreenState.BATTLE);
  }

  handleClick(x: number, y: number): void {
    console.log(`BattleSetup click at (${x}, ${y})`);
  }

  handleKeyDown(key: string): void {
    if (key === 'Escape') {
      this.context.onScreenChange(ScreenState.MAIN_MENU);
    } else if (key === 'ArrowUp') {
      const team = getCurrentTeam(this.gameState!);
      if (team) {
        this.selectedRosterIndex = Math.max(0, this.selectedRosterIndex - 1);
        this.selectedBattleSlot = -1;
      }
    } else if (key === 'ArrowDown') {
      const team = getCurrentTeam(this.gameState!);
      if (team) {
        this.selectedRosterIndex = Math.min(team.gladiatorIds.length - 1, this.selectedRosterIndex + 1);
        this.selectedBattleSlot = -1;
      }
    } else if (key === 'Enter' || key === ' ') {
      this.tryAddSelectedToBattle();
    }
  }
}
